






















import { mixins } from 'vue-class-component'
import { Component, Prop, Emit, Watch } from 'vue-property-decorator'
import { showErrors, showNotification } from '@/utils/snack'
import { SnackType } from '@/interfaces/snack'
import AppDialog from '@/components/AppDialog.vue'
import AppSteps from '@/components/AppSteps.vue'
import QuoteFormCarrier from '@/modules/Quote/components/forms/QuoteFormCarrier.vue'
import QuoteFormPremium from '@/modules/Quote/components/forms/QuoteFormPremium.vue'
import RecordTypesHandler from '@/mixins/record-types'
import { newQuotePremium, editQuotePremium } from '@/modules/Quote/gql/quote.gql'
import SearchCarrierProduct from '@/mixins/search-queries/search-carrier-product'
import { IQuotePremiumInput, IQuoteCarrierInput } from '@/modules/Quote/interfaces/quote-form'
import { billingTypeKeys, payerTypeKeys } from '@/modules/Policy/helpers/policy'
import { QuoteWithPremium } from '@/modules/Quote/interfaces/quote-with-premium'
import { CarrierProductCommission } from '@/modules/CarrierProduct/interfaces/carrier-product-commission'

@Component({
  components: {
    QuoteFormCarrier,
    QuoteFormPremium,
    AppDialog,
    AppSteps
  }
})
export default class QuoteRequestForm extends mixins(RecordTypesHandler, SearchCarrierProduct) {
  @Prop(Boolean) value!: boolean
  @Prop(Number) readonly step!: number
  @Prop({ type: Object as () => QuoteWithPremium }) readonly quote!: QuoteWithPremium
  @Prop(Boolean) readonly create!: boolean

  showDialog = false
  loading = false

  recordTypeIndex = 0
  stepIndex = 0

  disableSwitcher = false

  ifGA = true

  steps = [
    ['Choose Carrier', 770, 331],
    ['Premium Info', 770, 940]
  ]

  carrierInput: IQuoteCarrierInput = {
    carrier: {},
    ga: {}
  }

  premiumInput: IQuotePremiumInput = {
    effectiveDate: '',
    expirationDate: '',
    closeDate: '',
    term: 0,
    payerTypeIdx: 0,
    premium: '0',
    agencyFee: '0',
    carrierPolicyFee: '0',
    inspectionFee: '0',
    stampingFee: '0',
    taxesFee: '0',
    brokerFee: '0',
    billingTypeIdx: 0,
    commission: '0',
    installmentPlan: 0
  }

  filteredCommissions: CarrierProductCommission[] = []

  get stepItems () {
    return this.steps.map(st => st[0])
  }

  get confirmText () {
    return this.stepIndex < this.steps.length - 1
      ? 'Next'
      : this.quote.premiumInfo
        ? 'Save'
        : 'Create'
  }

  @Watch('value')
  onValueChanged (val: boolean) {
    if (val) {
      this.resetStep()
      this.getPremiumCommissions()
      this.initInput()
    }
    this.showDialog = val
  }

  @Watch('stepIndex')
  onStepChanged (val: number) {
    const {
      carrierInput: {
        carrier,
        ga
      }
    } = this

    if (val !== 1) {
      return
    }

    this.filteredCommissions = this.candidatesCommission.filter(commission => {
      let hit = false
      if (ga.id && carrier.id) {
        hit = +commission.gaId === ga.id &&
          +commission.carrierId === carrier.id
      } else if (ga.id) {
        hit = +commission.gaId === ga.id &&
          !commission.carrierId
      } else {
        hit = +commission.carrierId === carrier.id &&
          !commission.gaId
      }

      return hit
    })
  }

  resetStep () {
    this.stepIndex = this.step
  }

  async getPremiumCommissions () {
    const { carrierProduct } = this.quote
    if (!carrierProduct) {
      return
    }
    await this.getCandidateCommissions(+carrierProduct.id)
  }

  initInput () {
    if (this.quote.premiumInfo) {
      this.disableSwitcher = true
      const {
        carrierId, carrierName,
        gaId, gaName,
        effectiveDate, closeDate, expirationDate,
        installmentPlan,
        billingType, term, premium, agencyFee, brokerFee, carrierPolicyFee, inspectionFee, taxesFee, stampingFee, commission, payerType
      } = this.quote.premiumInfo

      this.carrierInput = {
        carrier: {
          name: carrierName || '',
          id: carrierId ? +carrierId : undefined
        },
        ga: {
          name: gaName || '',
          id: gaId ? +gaId : undefined
        }
      }

      this.premiumInput = {
        effectiveDate: effectiveDate ? effectiveDate + '' : '',
        expirationDate: expirationDate ? expirationDate + '' : '',
        closeDate: closeDate ? closeDate + '' : '',
        term: term || 1,
        payerTypeIdx: payerTypeKeys.findIndex(key => key === payerType),
        premium: premium || '0',
        agencyFee: agencyFee || '0',
        carrierPolicyFee: carrierPolicyFee || '0',
        inspectionFee: inspectionFee || '0',
        stampingFee: stampingFee || '0',
        taxesFee: taxesFee || '0',
        brokerFee: brokerFee || '0',
        billingTypeIdx: billingTypeKeys.findIndex(key => key === billingType),
        commission: commission ? commission + '' : '0',
        installmentPlan: installmentPlan || 1
      }
      this.ifGA = !!this.quote.premiumInfo.gaId
    } else {
      this.ifGA = true
      this.disableSwitcher = false
      this.clearForm()
    }
  }

  handleStepClick (index: number) {
    if (!this.quote.premiumInfo) {
      return
    }

    const pass = this.handleValidate()
    if (!pass) {
      return
    }

    this.stepIndex = index
  }

  handleValidate () {
    const { stepIndex, steps } = this
    const refName = steps[stepIndex][0] as string
    return (this.$refs[refName] as Vue & {
      validate: () => null | { [key: string]: number | string }
    }).validate()
  }

  handleConfirm (event: Event, finishAhead = false) {
    const pass = this.handleValidate()
    if (!pass) {
      return
    }

    if (this.stepIndex === this.steps.length - 1 || finishAhead) {
      if (this.create) {
        this.handleCreate()
      } else {
        this.handleUpdate()
      }
    } else {
      this.stepIndex += 1
    }
  }

  @Emit('back')
  handleBack () {
    if (this.stepIndex === 0) {
      this.handleCancel()
      return
    }
    this.stepIndex -= 1
  }

  async handleCreate () {
    const {
      carrierInput: {
        carrier: { id: carrierId, name: carrierName },
        ga: { id: gaId, name: gaName }
      },
      premiumInput: {
        term, installmentPlan,
        effectiveDate, expirationDate, closeDate,
        premium, agencyFee, carrierPolicyFee, inspectionFee, stampingFee, brokerFee, taxesFee, commission, billingTypeIdx, payerTypeIdx
      }
    } = this
    this.loading = true

    try {
      const res = await this.$apollo.mutate({
        mutation: newQuotePremium,
        variables: {
          input: {
            carrierId,
            term: term ? Number(term) : null,
            billingType: billingTypeKeys[billingTypeIdx] || null,
            closeDate,
            payerType: payerTypeKeys[payerTypeIdx] || null,
            effectiveDate: effectiveDate ? effectiveDate + '' : null,
            expirationDate: expirationDate ? expirationDate + '' : null,
            installmentPlan: installmentPlan ? Number(installmentPlan) : null,
            premium: premium ? +premium : 0,
            agencyFee: agencyFee ? +agencyFee : 0,
            carrierPolicyFee: carrierPolicyFee ? +carrierPolicyFee : 0,
            inspectionFee: inspectionFee ? +inspectionFee : 0,
            stampingFee: stampingFee ? +stampingFee : 0,
            brokerFee: brokerFee ? +brokerFee : 0,
            taxesFee: taxesFee ? +taxesFee : 0,
            commission: commission ? +commission : 0,
            gaId,
            quoteId: +this.quote.id,
            gaName,
            carrierName
          }
        }
      })
      this.clearForm()
      this.onSuccess('Type in quote request successfully')
    } catch (e) {
      this.loading = false
      showErrors(e.graphQLErrors)
    }
  }

  async handleUpdate () {
    const {
      carrierInput: {
        carrier: { id: carrierId, name: carrierName }
      },
      premiumInput: {
        term, installmentPlan,
        effectiveDate, expirationDate, closeDate,
        premium, agencyFee, carrierPolicyFee, inspectionFee, stampingFee, brokerFee, taxesFee, commission, billingTypeIdx, payerTypeIdx
      }
    } = this
    this.loading = true

    try {
      const res = await this.$apollo.mutate({
        mutation: editQuotePremium,
        variables: {
          relationId: Number(this.quote.premiumInfo.id),
          input: {
            carrierId,
            term: term ? Number(term) : null,
            payerType: payerTypeKeys[payerTypeIdx] || null,
            billingType: billingTypeKeys[billingTypeIdx] || null,
            closeDate,
            effectiveDate: effectiveDate ? effectiveDate + '' : null,
            expirationDate: expirationDate ? expirationDate + '' : null,
            installmentPlan: installmentPlan ? Number(installmentPlan) : null,
            premium: premium ? +premium : 0,
            agencyFee: agencyFee ? +agencyFee : 0,
            carrierPolicyFee: carrierPolicyFee ? +carrierPolicyFee : 0,
            inspectionFee: inspectionFee ? +inspectionFee : 0,
            stampingFee: stampingFee ? +stampingFee : 0,
            brokerFee: brokerFee ? +brokerFee : 0,
            taxesFee: taxesFee ? +taxesFee : 0,
            commission: commission ? +commission : 0,
            carrierName
          }
        }
      })
      this.clearForm()
      this.onSuccess('Edit quote premium information successfully')
    } catch (e) {
      this.loading = false
      showErrors(e.graphQLErrors)
    }
  }

  clearForm () {
    this.loading = false
    this.carrierInput = {
      carrier: {
        id: undefined,
        name: ''
      },
      ga: {
        id: undefined,
        name: ''
      }
    }

    this.premiumInput = {
      effectiveDate: '',
      expirationDate: '',
      closeDate: '',
      term: 1,
      payerTypeIdx: 0,
      premium: '0',
      agencyFee: '0',
      carrierPolicyFee: '0',
      inspectionFee: '0',
      stampingFee: '0',
      taxesFee: '0',
      brokerFee: '0',
      billingTypeIdx: 0,
      commission: '0',
      installmentPlan: 1
    }
  }

  @Emit('cancel')
  handleCancel () {
    this.$emit('input', false)
    this.clearForm()
  }

  @Emit('success')
  onSuccess (msg = '') {
    this.$emit('input', false)
    this.resetStep()
    if (msg) {
      showNotification({
        msg,
        type: SnackType.Success
      })
    }
  }
}
